import { FragmentType, getFragmentData, graphql } from '../../../gql';
import { ComponentProps, FC } from 'react';
import { CreatableSelect } from '../CreatableSelect';
import { useIntl } from 'react-intl';
import { groupBy } from 'lodash';

const metricTemplateFragment = graphql(`
	fragment CreatableMetricTemplateSelect_MetricTemplate on MetricTemplate {
		id
		name
		isSystem
	}
`);

type MetricTemplateFrament = FragmentType<typeof metricTemplateFragment>;

type Props = Omit<
	ComponentProps<typeof CreatableSelect>,
	'items' | 'sections' | 'createItemTitle' | 'createDialogTitle' | 'emptyItemErrorMessage'
> &
	Readonly<{
		metricTemplates: readonly MetricTemplateFrament[];
	}>;

const CreatableMetricTemplateSelect: FC<Props> = ({ value, loading, metricTemplates, ...props }) => {
	const intl = useIntl();

	const items = metricTemplates
		.map((metricTemplate) => getFragmentData(metricTemplateFragment, metricTemplate))
		.sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
		.map(({ id, name, isSystem }) => ({ value: id, label: name, isSystem }));

	const { true: systemItems = [], false: customItems = [] } = groupBy(items, 'isSystem');

	const sections = [
		{
			header: intl.formatMessage({ id: 'metric-template-select.subheader.system' }),
			items: systemItems,
		},
		{
			header: intl.formatMessage({ id: 'metric-template-select.subheader.custom' }),
			items: customItems,
		},
	];

	return (
		<CreatableSelect
			{...props}
			loading={loading}
			value={value || '' /* value cannot be undefined in order to keep the input controlled */}
			sections={sections}
			createItemTitle={intl.formatMessage({ id: 'create-metric-template.item.title' })}
			createDialogTitle={intl.formatMessage({ id: 'create-metric-template.dialog.title' })}
			emptyItemErrorMessage={intl.formatMessage({ id: 'create-metric-template.error.empty-name' })}
		/>
	);
};

export { CreatableMetricTemplateSelect };
